import {
    Component,
    inject,
    OnInit
} from '@angular/core';

import {
    restManager,
    $state
} from 'app/ajs-upgraded-providers';

import {
    ConfirmDialogService,
} from "src/app/dialogs";

import { TranslateService } from "@ngx-translate/core";
import { TilbyToolbar } from 'src/app/models';
import {
    ErrorsLoggerService,
    ToolbarEventsService
} from 'src/app/core/services';
import { GiftcardsTypes } from 'tilby-models';
import { GiftcardsTypesFormGroup } from '../giftcard-type-form';
import { GiftcardTypeFormService } from '../../services';
import _ from 'lodash';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';

@Component({
	standalone: false,
    selector: 'giftcard-types-panel',
    templateUrl: './giftcard-types-panel.component.html',
    styleUrls: ['./giftcard-types-panel.component.scss'],
    providers: []
})
export class GiftcardTypesPanelComponent implements OnInit, TilbyToolbar {
    private readonly _snackBar = inject(MatSnackBar);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly errorsLoggerService = inject(ErrorsLoggerService);
    private readonly giftcardTypeFormService = inject(GiftcardTypeFormService);
    private readonly restManagerService = inject(restManager);
    private readonly state = inject($state);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly translate = inject(TranslateService);

    giftcardTypesList: Array<GiftcardsTypes> = [];
    giftcardTypesForm?: CustomFormGroup<GiftcardsTypesFormGroup>;
    allWithCheckbox = false;
    pristineGiftcardType: GiftcardsTypes = {
        name: this.translate.instant('GIFTCARD.TYPES_PANEL.NEW_TYPE'),
        is_divisible: false,
        expire_after: 365,
        origin_environment_only: false
    };
    selectedGiftcardType: GiftcardsTypes = this.pristineGiftcardType;
    isNewEntryInProgress = false;
    firstEntry = false;
    selected = false;

    async ngOnInit() {
        this.createToolbarButtons();
        this.setModuleTitle();
        try {
            const results = await this.restManagerService.getList(['giftcards_types']);
            // results potrebbe essere un oggetto con errore type="not found" se non ci sono risultati
            if(Array.isArray(results)) {
                this.giftcardTypesList = _.orderBy(results, ['created_at'], ['asc']);
            }
        } catch (error) {
            this.errorsLoggerService.err(error, 'GIFTCARD_TYPES');
            this.giftcardTypesList = [];
        }
    }

    setModuleTitle() {
        this.toolbarEventsService.moduleTitle.next('GIFTCARD_TYPES');
    }

    createToolbarButtons() {
        this.toolbarEventsService.buttons$.next({
            barButtons: [],
            panelButtons: [
                {
                    isIt: () => true,
                    name: 'open_giftcard',
                    icon: () => 'card_membership',
                    label: () => 'SIDENAV.MODULES.GIFTCARD',
                    click: () => this.state.go("app.new.giftcard")
                },
            ]
        });
    }

    back() {
        this.state.go("app.new.giftcard");
    }

    private async unsave(giftcardType: GiftcardsTypes) {
        const confirm = await this.confirmDialogService.openDialog({
            data: {
                messageLabel: 'GIFTCARD.TYPES_PANEL.QUESTION_SELECT',
                confirmLabel: 'GIFTCARD.TYPES_PANEL.CONFIRM_LABEL',
                cancelLabel: 'GIFTCARD.TYPES_PANEL.CANCEL_LABEL'
            }
        });

        if (!confirm) {
            return;
        }

        if(this.isNewEntryInProgress) {
            // elimina bozza
            this.giftcardTypesList.pop();
            this.isNewEntryInProgress = false;
        }

        this.giftcardTypesForm?.markAsPristine();
        this.select(giftcardType, false);
    }

    select(giftcardType: GiftcardsTypes, isFormDirty = false) {
        if(isFormDirty || this.isNewEntryInProgress) {
            this.unsave(giftcardType);
        } else {
            const _giftcardType = structuredClone(giftcardType);
            this.pristineGiftcardType = _giftcardType;
            this.selectedGiftcardType = _giftcardType;
            if(!this.selected) {
                this.giftcardTypesForm = this.giftcardTypeFormService.createForm(_giftcardType);
            } else {
                this.giftcardTypeFormService.updateForm(_giftcardType);
            }
        }
        this.selected = true;
    }

    isSelected(sel: GiftcardsTypes): boolean {
        return sel.uuid === this.pristineGiftcardType.uuid;
    }

    addNew(){
        const newGiftcardType: GiftcardsTypes = {
            name: this.translate.instant('GIFTCARD.TYPES_PANEL.NEW_TYPE'),
            is_divisible: false,
            expire_after: 365
        };

        this.giftcardTypesList.push(newGiftcardType);
        this.select(newGiftcardType);
        // importante prima fare la select
        this.isNewEntryInProgress = true;
    }

    async save() {
        const giftcardTypeToSave: GiftcardsTypes = { ...this.selectedGiftcardType, ...this.generalFormGroup?.value, print_options: this.printOptionsFormGroup?.value };
        let res: GiftcardsTypes;
        let success: boolean = false;
        if(this.isNewEntryInProgress) {
            // POST
            res = await this.restManagerService.post("giftcards_types", giftcardTypeToSave);
            if(res) {
                success = true;
                const lastIndex = this.giftcardTypesList.length - 1;
                this.giftcardTypesList[lastIndex] = res;
                this.isNewEntryInProgress = false;
                if(this.firstEntry) {
                    this.firstEntry = false;
                }
                this.giftcardTypesForm?.markAsPristine();
                this.select(res);
            }
        } else {
            // PUT
            const {uuid} = this.selectedGiftcardType;
            if(uuid) {
                res = await this.restManagerService.put("giftcards_types", uuid, giftcardTypeToSave);
                if (res) {
                    success = true;
                    // cercare l'indice
                    const index = _.findIndex(this.giftcardTypesList, {uuid});
                    if(index !== -1) {
                        this.giftcardTypesList[index] = res;
                        this.giftcardTypesForm?.markAsPristine();
                        this.select(res);
                    }
                }
            }
        }
        if (success) {
            const labelSuccess = this.translate.instant('GIFTCARD.TYPES_PANEL.SUCCESS_SAVE');
            this._snackBar.open(labelSuccess, undefined, { duration: 3000, horizontalPosition: 'left', verticalPosition: 'bottom' });
        } else {
            const labelError = this.translate.instant('GIFTCARD.TYPES_PANEL.ERROR_SAVE');
            this._snackBar.open(labelError, undefined, { duration: 5000, horizontalPosition: 'left', verticalPosition: 'bottom' });
        }

    }

    async delete(){
        const callbackFunction = async (giftcardTypes: GiftcardsTypes) => {
            let labelDelete: string;
            if(this.isNewEntryInProgress) {
                this.giftcardTypesList.pop();
                this.isNewEntryInProgress = false;
                labelDelete = this.translate.instant('GIFTCARD.TYPES_PANEL.SUCCESS_DELETE_DRAFT');
                this._snackBar.open(labelDelete, undefined, { duration: 3000, horizontalPosition: 'left', verticalPosition: 'bottom' });
            } else {
                // delete da remoto
                const target = _.cloneDeep(giftcardTypes);
                let res:any = await this.restManagerService.deleteOne("giftcards_types", target.uuid);
                if(res && res.message === 'ok') {
                    const index = _.findIndex(this.giftcardTypesList, {uuid: target.uuid});
                    _.pullAt(this.giftcardTypesList, [index]);
                    labelDelete = this.translate.instant('GIFTCARD.TYPES_PANEL.SUCCESS_DELETE_REMOTE');
                    this._snackBar.open(labelDelete, undefined, { duration: 3000, horizontalPosition: 'left', verticalPosition: 'bottom' });
                }
            }
            this.giftcardTypesForm?.markAsPristine();
            this.selected = false;
        };

        const res = await this.confirmDialogService.openDialog({
            data: {
                messageLabel: 'GIFTCARD.TYPES_PANEL.QUESTION_DELETE',
                confirmLabel: 'GIFTCARD.TYPES_PANEL.CONFIRM_LABEL',
                cancelLabel: 'GIFTCARD.TYPES_PANEL.CANCEL_LABEL'
            }
        });
        
        if(res){
            await callbackFunction(this.selectedGiftcardType);
        }
    }

    get generalFormGroup() {
        return this.giftcardTypesForm?.controls.general;
    }

    get printOptionsFormGroup() {
        return this.giftcardTypesForm?.controls.print_options;
    }

    get name() {
        return this.generalFormGroup?.controls.name;
    }

    get description() {
        return this.generalFormGroup?.controls.description;
    }

    get expire_after() {
        return this.generalFormGroup?.controls.expire_after;
    }

    get is_divisible() {
        return this.generalFormGroup?.controls.is_divisible;
    }

}
